import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { BookingsService } from '../../../core/services/bookings.service';
import { BookingImportDetail } from '../../../core/models/entities/booking-import';
import { BreadcrumbItem } from '../../../shared-manager/components/page-header.component';

@Component({
  selector: 'app-booking-import-detail-page',
  template: `
    <app-page-header
      [title]="
        importDetail()?.file_name ?? ('bookings.import.detail-title' | transloco)
      "
      [breadcrumbs]="breadcrumbs"
    ></app-page-header>

    <app-spacer></app-spacer>

    @if (importDetail(); as detail) {
      <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
        <div>{{
          'bookings.import.imported-on'
            | transloco: { date: detail.created_at | date: 'dd.MM.yyyy HH:mm' }
        }}</div>
        @if (detail.user) {
          <div>{{
            'bookings.import.imported-by' | transloco: { name: detail.user.name }
          }}</div>
        }
        <div>{{
          (detail.rows_count === 1
            ? 'bookings.import.count-one'
            : 'bookings.import.count-other'
          ) | transloco: { count: detail.rows_count }
        }}</div>
        <div>
          @if (detail.rolled_back_at) {
            <app-badge variant="gray">{{
              'bookings.import.status-cancelled' | transloco
            }}</app-badge>
          } @else {
            <app-badge variant="success">{{
              'bookings.import.status-imported' | transloco
            }}</app-badge>
          }
        </div>
      </div>

      <app-spacer size="6"></app-spacer>
    }

    <app-table
      [data]="importDetail()?.bookings ?? []"
      [loading]="loading()"
      [clickableRows]="false"
      [emptyStateMessage]="'bookings.import.detail-empty' | transloco"
    >
      <app-table-column
        field="reference"
        [header]="'bookings.import.col-reference' | transloco"
        width="110px"
      >
        <ng-template let-booking>{{ booking.reference }}</ng-template>
      </app-table-column>
      <app-table-column
        field="room_name"
        [header]="'bookings.import.col-room' | transloco"
      >
        <ng-template let-booking>{{ booking.room_name ?? '-' }}</ng-template>
      </app-table-column>
      <app-table-column
        field="name"
        [header]="'bookings.import.col-title' | transloco"
      >
        <ng-template let-booking>{{ booking.name }}</ng-template>
      </app-table-column>
      <app-table-column
        field="start"
        [header]="'bookings.import.col-start' | transloco"
      >
        <ng-template let-booking>{{
          booking.start | date: "EEE dd.MM.yyyy, HH'h'mm"
        }}</ng-template>
      </app-table-column>
      <app-table-column
        field="end"
        [header]="'bookings.import.col-end' | transloco"
      >
        <ng-template let-booking>{{
          booking.end | date: "EEE dd.MM.yyyy, HH'h'mm"
        }}</ng-template>
      </app-table-column>
      <app-table-column
        field="comment"
        [header]="'bookings.import.col-comment' | transloco"
      >
        <ng-template let-booking>
          <span [innerHTML]="booking.comment ?? '-'"></span>
        </ng-template>
      </app-table-column>
    </app-table>
  `,
  styles: ``,
  standalone: false,
})
export class BookingImportDetailPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private bookingsService = inject(BookingsService);
  private route = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);

  importDetail = signal<BookingImportDetail | null>(null);
  loading = signal(false);

  breadcrumbs: BreadcrumbItem[] = [];

  async ngOnInit() {
    const importId = Number(this.route.snapshot.paramMap.get('id'));

    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.breadcrumbs = [
          {
            label: this.translocoService.translate(
              'bookings.import.breadcrumb-parent',
            ),
            routerLink: '/reservations',
          },
          {
            label: this.translocoService.translate(
              'bookings.import.breadcrumb-self',
            ),
            routerLink: '/reservations/import',
          },
          {
            label: this.translocoService.translate(
              'bookings.import.breadcrumb-detail',
            ),
          },
        ];
      });

    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.bookingsService.findImport(importId),
      );
      this.importDetail.set(response.data);
    } finally {
      this.loading.set(false);
    }
  }
}
